import { useRouter } from "expo-router";
import { useAtomValue } from "jotai";
import { useMemo, useRef, useState } from "react";
import {
  Platform,
  Pressable,
  ScrollView,
  StyleSheet,
  View,
} from "react-native";

import type { EventEdit } from "@/hooks/use-event-edit";
import type { EventNode } from "@/types/event";
import type { DisplayEvent } from "@/utils/event-helpers";
import type { EventGuest } from "@/utils/event-queries";

import { sessionAtom } from "@/atoms/session";
import AddToCalendar from "@/components/add-to-calendar";
import { DarkBlurCard } from "@/components/DarkBlurCard";
import DisplayDescription from "@/components/DisplayDescription";
import BottomEventButton from "@/components/event/BottomEventButton";
import GuestListModal from "@/components/event/guest-list-modal";
import RsvpModal from "@/components/event/rsvp-modal";
import { SectionEditModal } from "@/components/event/section-edit-modal";
import { GText } from "@/components/g-text";
import { GPressable } from "@/components/GPressable";
import AppModal from "@/components/modal";
import OpenDirections from "@/components/open-directions";
import SignInModal from "@/components/sign-in/sign-in-modal";
import { CalendarIcon } from "@/components/ui/calendar-icon";
import { PinIcon } from "@/components/ui/pin-icon";
import { TicketIcon } from "@/components/ui/ticket-icon";
import { colors } from "@/constants/colors";
import Bugsnag from "@/utils/bugsnag";
import {
  composeAddress,
  formatEventDate,
  formatGuestList,
  formatHostsBeingShown,
  formatPriceEditMode,
  formatPriceViewMode,
  formatTimeRange,
} from "@/utils/copy";
import { upsertRsvpStatus } from "@/utils/event-queries";
import { ACCEPT_STRING, validateAndUploadImage } from "@/utils/image-upload";

const HEADER_HEIGHT = 60;

const HORIZONTAL_MARGIN = 10;

// ── Types ────────────────────────────────────────────────────────────

interface EventDisplayProps {
  event: EventNode;
  eventEdit?: EventEdit;
  guests: EventGuest[];
  hosts: string[];
  mode: "edit" | "view";
  myRsvp: { role: string; rsvp_status: string } | null;
  onFlyerUrlChange?: (url: string) => void;
  onGetTicket?: () => void;
  onRefreshMember?: (userId: string) => Promise<void>;
  onRemoveTicket?: () => void;
  onShareEvent?: () => void;
  paymentUI?: React.ReactNode;
  ticketLoading?: boolean;
  viewEvent?: DisplayEvent;
}

// ── Helpers ──────────────────────────────────────────────────────────

export function canPublish(node: EventNode): boolean {
  return !!(
    node.title?.trim() &&
    node.starts_at &&
    node.ends_at &&
    node.location_name?.trim()
  );
}

// ── Main component ──────────────────────────────────────────────────

export function EventDisplay({
  event,
  eventEdit,
  guests,
  hosts,
  mode,
  myRsvp,
  onFlyerUrlChange,
  onGetTicket,
  onRefreshMember,
  onRemoveTicket,
  onShareEvent,
  paymentUI,
  ticketLoading,
  viewEvent,
}: EventDisplayProps) {
  const vScale = 1;
  const iconSize = 32;
  const iconColWidth = 40;
  const router = useRouter();
  const sessionState = useAtomValue(sessionAtom);
  const session = sessionState === "loading" ? null : sessionState;
  const editable = mode !== "view";
  const viewCursor =
    Platform.OS === "web" ? { cursor: "default" as const } : {};

  const saving = eventEdit?.saving ?? false;
  const error = eventEdit?.error ?? null;

  const [modalSection, setModalSection] = useState<
    "date" | "description" | "location" | "ticket" | "title" | null
  >(null);
  const [guestListVisible, setGuestListVisible] = useState(false);
  const [rsvpVisible, setRsvpVisible] = useState(false);
  const [signInVisible, setSignInVisible] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadStatus, setUploadStatus] = useState<
    "compressing" | "idle" | "uploading"
  >("idle");
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadError, setUploadError] = useState<null | string>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const myStatus = myRsvp?.rsvp_status ?? null;
  const isAdmin = myRsvp?.role === "admin";

  // Ticket kind from event node
  const ticketKind = event.event_ticket_kindCollection?.edges[0]?.node ?? null;
  const hasTicket = ticketKind != null && ticketKind.price_cents > 0;
  const isConfirmed = mode === "view" && myStatus === "confirmed";

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    setUploadProgress(0);
    setUploadStatus("idle");
    setUploadError(null);
    try {
      const url = await validateAndUploadImage(
        file,
        session!.access_token,
        (p) => setUploadProgress(p),
        (status) => setUploadStatus(status),
      );
      eventEdit?.patchEvent({ flyer_url: url });
      onFlyerUrlChange?.(url);
    } catch (err) {
      Bugsnag.notify(err instanceof Error ? err : new Error(String(err)));
      setUploadError(
        err instanceof Error ? err.message.toLowerCase() : "upload failed",
      );
    } finally {
      setUploading(false);
      setUploadStatus("idle");
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const handleRsvp = async (choice: "cancelled" | "confirmed" | "maybe") => {
    Bugsnag.leaveBreadcrumb("[handleRsvp] called", { choice });
    setRsvpVisible(false);
    if (!event || !session) return;
    const ok = await upsertRsvpStatus(
      event.id,
      session.user.id,
      choice,
      ticketKind?.id,
    );
    if (ok) {
      await onRefreshMember?.(session.user.id);
    }
  };

  // Derive display values
  const displayTitle = event.title ?? "";
  const displayStartISO = event.starts_at ?? "";
  const displayEndISO = event.ends_at ?? "";

  const displayVenue =
    mode === "view" && viewEvent
      ? isConfirmed
        ? viewEvent.venue.toLowerCase()
        : [viewEvent.locationNeighborhood, viewEvent.locationCity]
            .filter(Boolean)
            .join(", ")
            .toLowerCase() || viewEvent.venue.toLowerCase()
      : (event.location_name || "location").toLowerCase();

  const displayAddress =
    mode === "view" && viewEvent
      ? isConfirmed
        ? viewEvent.address
        : "address shown after rsvp"
      : composeAddress(event) || "add address";

  const displayPrice =
    mode === "view" && viewEvent
      ? formatPriceViewMode(viewEvent.priceCents, viewEvent.priceServiceFee)
      : ticketKind == null || ticketKind.price_cents === 0
        ? "free"
        : `you'll get ${formatPriceEditMode(String(ticketKind.price_cents))} per person`;

  const displayPriceDesc =
    mode === "view" && viewEvent
      ? viewEvent.ticketsRemaining != null && viewEvent.ticketsRemaining < 20
        ? `${viewEvent.ticketsRemaining} left`
        : viewEvent.priceServiceFee > 0
          ? `includes $${viewEvent.priceServiceFee % 100 === 0 ? viewEvent.priceServiceFee / 100 : (viewEvent.priceServiceFee / 100).toFixed(2)} service cost`
          : viewEvent.priceDescription
      : ticketKind && ticketKind.price_cents > 0
        ? "a service fee will be added to cover processing costs"
        : null;

  const displayDescription = event.description ?? "";

  const dateAction = mode === "view" ? "add to calendar" : "edit";
  const venueAction =
    mode === "view"
      ? isConfirmed
        ? "directions"
        : hasTicket
          ? "get ticket"
          : "rsvp"
      : "edit";
  const priceAction =
    mode === "view" ? (isConfirmed ? "share event" : "get ticket") : "edit";

  const originalStatus = event.status;

  // Wrap info rows for view mode actions vs edit mode modals
  const wrapDateRow = (children: React.ReactNode) => {
    if (mode === "view" && viewEvent) {
      return (
        <AddToCalendar
          description={viewEvent.description}
          endISO={viewEvent.endISO ?? ""}
          geo={viewEvent.geo}
          location={viewEvent.address}
          startISO={viewEvent.startISO ?? ""}
          title={viewEvent.title}
          venue={viewEvent.venue}
        >
          {children}
        </AddToCalendar>
      );
    }
    return (
      <Pressable
        onPress={() => setModalSection("date")}
        style={({ pressed }: { hovered?: boolean; pressed: boolean }) => [
          pressed && styles.rowHighlight,
        ]}
      >
        {children}
      </Pressable>
    );
  };

  const wrapVenueRow = (children: React.ReactNode) => {
    if (mode === "view" && viewEvent) {
      if (isConfirmed) {
        return (
          <OpenDirections
            label={viewEvent.venue}
            lat={viewEvent.geo.lat}
            lng={viewEvent.geo.lng}
            placeId={viewEvent.placeId}
          >
            {children}
          </OpenDirections>
        );
      }
      return (
        <Pressable
          onPress={hasTicket ? onGetTicket : () => setRsvpVisible(true)}
        >
          {children}
        </Pressable>
      );
    }
    return (
      <Pressable
        onPress={() => setModalSection("location")}
        style={({ pressed }: { hovered?: boolean; pressed: boolean }) => [
          pressed && styles.rowHighlight,
        ]}
      >
        {children}
      </Pressable>
    );
  };

  const wrapPriceRow = (children: React.ReactNode) => {
    if (mode === "view") {
      return (
        <Pressable onPress={isConfirmed ? onShareEvent : onGetTicket}>
          {children}
        </Pressable>
      );
    }
    return (
      <Pressable
        onPress={() => {
          if (!hasTicket) {
            eventEdit?.patchTicketKind({ price_cents: 0 });
          }
          setModalSection("ticket");
        }}
        style={({ pressed }: { hovered?: boolean; pressed: boolean }) => [
          pressed && styles.rowHighlight,
        ]}
      >
        {children}
      </Pressable>
    );
  };

  const getBottomLabel = useMemo(() => {
    if (event.status === "cancelled") return "back to events";

    Bugsnag.leaveBreadcrumb("event status", { status: event.status });
    if (mode === "view") {
      if (!session) return "login";
      if (isAdmin && event.status !== "published") return "continue editing";
      if (ticketLoading) return "processing...";

      if (!isConfirmed) {
        if (hasTicket) {
          if (isAdmin) return "rsvp";
          return "get ticket";
        }
        return "rsvp";
      }
      return "share event";
    }
    if (!event.title?.trim()) return "add title";
    if (!event.starts_at || !event.ends_at) return "add date";
    if (!event.location_name?.trim()) return "add location";
    if (event.status !== "published") return "publish event";
    return "finish with edits";
  }, [
    event.ends_at,
    event.location_name,
    event.starts_at,
    event.status,
    event.title,
    hasTicket,
    isAdmin,
    isConfirmed,
    mode,
    session,
    ticketLoading,
  ]);

  const handleBottomOnPress = async () => {
    switch (getBottomLabel) {
      case "add date":
        setModalSection("date");
        break;
      case "add location":
        setModalSection("location");
        break;
      case "add title":
        setModalSection("title");
        break;
      case "back to events":
        router.navigate("/");
        break;
      case "continue editing":
        router.setParams({ mode: "edit" });
        break;
      case "finish with edits":
        await eventEdit?.persistNow();
        router.setParams({ mode: undefined });
        break;
      case "get ticket":
        onGetTicket?.();
        break;
      case "login":
        setSignInVisible(true);
        break;
      case "publish event":
        eventEdit?.patchEvent({ status: "published" });
        await eventEdit?.persistNow();
        break;
      case "rsvp":
        setRsvpVisible(true);
        break;
      case "share event":
        onShareEvent?.();
        break;
    }
  };

  const displayError = uploadError ?? error;

  return (
    <View style={styles.container}>
      {/* Fixed header */}
      <View style={styles.fixedHeader}>
        <GPressable
          blurStyle={styles.editButton}
          onPress={() => {
            if (editable) {
              router.setParams({ mode: undefined });
            } else {
              router.navigate("/e");
            }
          }}
          style={{ marginRight: "auto" }}
        >
          <GText style={styles.editText}>
            {!editable ? "see all events" : "back to view mode"}
          </GText>
        </GPressable>
        <View style={styles.headerSpacer} />

        {isAdmin && !editable && (
          <GPressable
            blurStyle={styles.editButton}
            onPress={() => router.setParams({ mode: "edit" })}
            style={{ marginLeft: "auto" }}
          >
            <GText style={styles.editText}>edit</GText>
          </GPressable>
        )}
        {isAdmin && editable && originalStatus !== "cancelled" && (
          <GPressable
            blurStyle={styles.statusPill}
            onPress={() => setShowCancelConfirm(true)}
            style={{ marginLeft: "auto" }}
          >
            <GText style={styles.statusPillText}>cancel event</GText>
          </GPressable>
        )}
        {isAdmin && editable && originalStatus === "cancelled" && (
          <View
            style={[
              styles.statusPill,
              styles.statusPillCancelled,
              { marginLeft: "auto" },
            ]}
          >
            <GText
              style={[styles.statusPillText, styles.statusPillTextCancelled]}
            >
              event cancelled
            </GText>
          </View>
        )}
      </View>

      <ScrollView
        contentContainerStyle={[
          styles.content,
          {
            marginBottom: 100,
            marginTop: HEADER_HEIGHT,
          },
        ]}
        keyboardShouldPersistTaps="handled"
        showsVerticalScrollIndicator={false}
        style={[styles.scroll]}
      >
        <DarkBlurCard style={{ maxWidth: 600, padding: 20, width: "100%" }}>
          {/* Title */}
          <View style={styles.headerRow}>
            <Pressable
              disabled={!editable}
              onPress={() => setModalSection("title")}
              style={{ flexShrink: 1 }}
            >
              <GText
                nativeID="event-title"
                style={[
                  styles.title,
                  !editable && viewCursor,
                  editable && { color: colors.white },
                  editable && !displayTitle && { opacity: 0.5 },
                ]}
              >
                {editable ? displayTitle || "title" : displayTitle}
              </GText>
            </Pressable>
          </View>

          {/* Hosted by */}
          {hosts.length > 0 && (
            <GText style={[styles.hostedBy, viewCursor]}>
              {formatHostsBeingShown(hosts)}
            </GText>
          )}

          {/* Info grid */}
          <View style={styles.infoGrid}>
            {/* Date row */}
            {wrapDateRow(
              <View style={[styles.infoRow, { marginBottom: 12 * vScale }]}>
                <View style={[styles.iconColumn, { width: iconColWidth }]}>
                  <CalendarIcon color={colors.white} size={iconSize} />
                </View>
                <View style={styles.textColumn}>
                  <GText
                    style={[styles.detailPrimary, { fontSize: 15 * vScale }]}
                  >
                    {displayStartISO
                      ? formatEventDate(displayStartISO)
                      : "date"}
                  </GText>
                  {displayStartISO ? (
                    <GText
                      style={[
                        styles.detailSecondary,
                        { fontSize: 14 * vScale },
                      ]}
                    >
                      {formatTimeRange(displayStartISO, displayEndISO)}
                    </GText>
                  ) : null}
                </View>
                <GText style={[styles.actionText, { fontSize: 14 * vScale }]}>
                  {dateAction}
                </GText>
              </View>,
            )}

            {/* Venue row */}
            {wrapVenueRow(
              <View style={[styles.infoRow, { marginBottom: 12 * vScale }]}>
                <View style={[styles.iconColumn, { width: iconColWidth }]}>
                  <PinIcon color={colors.white} size={iconSize} />
                </View>
                <View style={styles.textColumn}>
                  <GText
                    style={[styles.detailPrimary, { fontSize: 15 * vScale }]}
                  >
                    {displayVenue}
                  </GText>
                  <GText
                    style={[styles.detailSecondary, { fontSize: 14 * vScale }]}
                  >
                    {displayAddress}
                  </GText>
                </View>
                <GText style={[styles.actionText, { fontSize: 14 * vScale }]}>
                  {venueAction}
                </GText>
              </View>,
            )}

            {/* Price row — hidden when no ticket exists (unless editable) */}
            {hasTicket
              ? wrapPriceRow(
                  <View style={[styles.infoRow, { marginBottom: 12 * vScale }]}>
                    <View style={[styles.iconColumn, { width: iconColWidth }]}>
                      <TicketIcon color={colors.white} size={iconSize} />
                    </View>
                    <View style={styles.textColumn}>
                      <GText
                        style={[
                          styles.detailPrimary,
                          { fontSize: 15 * vScale },
                        ]}
                      >
                        {displayPrice}
                      </GText>
                      {displayPriceDesc ? (
                        <GText
                          style={[
                            styles.detailSecondary,
                            { fontSize: 14 * vScale },
                          ]}
                        >
                          {displayPriceDesc}
                        </GText>
                      ) : null}
                    </View>
                    <GText
                      style={[styles.actionText, { fontSize: 14 * vScale }]}
                    >
                      {priceAction}
                    </GText>
                  </View>,
                )
              : editable
                ? wrapPriceRow(
                    <View
                      style={[styles.infoRow, { marginBottom: 12 * vScale }]}
                    >
                      <View
                        style={[styles.iconColumn, { width: iconColWidth }]}
                      >
                        <TicketIcon color={colors.white} size={iconSize} />
                      </View>
                      <View style={styles.textColumn}>
                        <GText
                          style={[
                            styles.detailPrimary,
                            { fontSize: 15 * vScale, opacity: 0.5 },
                          ]}
                        >
                          + add ticketing
                        </GText>
                      </View>
                    </View>,
                  )
                : null}
          </View>

          {/* Visibility + RSVP row */}
          <View style={styles.statusRow}>
            {editable ? (
              <>
                <GText style={styles.statusLabel}>visibility:</GText>
                <View style={styles.statusPillRow}>
                  {(
                    [
                      { label: "open to all", value: "public" },
                      {
                        label: "invite only",
                        value: "invite_only",
                      },
                    ] as const
                  ).map((opt) => {
                    const isActive = event.visibility === opt.value;
                    return (
                      <Pressable
                        disabled={isActive}
                        key={opt.value}
                        onPress={() =>
                          eventEdit?.patchEvent({ visibility: opt.value })
                        }
                        style={[
                          styles.statusPill,
                          isActive && styles.statusPillActive,
                        ]}
                      >
                        <GText
                          style={[
                            styles.statusPillText,
                            isActive && styles.statusPillTextActive,
                          ]}
                        >
                          {opt.label}
                        </GText>
                      </Pressable>
                    );
                  })}
                </View>
                <GText style={[styles.statusLabel, { opacity: 0 }]}>
                  event visibility:
                </GText>
              </>
            ) : (
              <>
                {session ? (
                  guests.length > 0 ? (
                    <Pressable
                      onPress={() => setGuestListVisible(true)}
                      style={{ flex: 1 }}
                    >
                      <GText style={styles.rsvpNames}>
                        {formatGuestList(guests)}
                      </GText>
                    </Pressable>
                  ) : (
                    <View style={{ flex: 1 }} />
                  )
                ) : (
                  <Pressable
                    onPress={() => setSignInVisible(true)}
                    style={{ flex: 1 }}
                  >
                    <GText style={styles.rsvpNames}>
                      login to see who&apos;s going
                    </GText>
                  </Pressable>
                )}
                <GText style={{ color: colors.light, ...viewCursor }}>
                  {event.visibility === "invite_only"
                    ? "invitation only"
                    : "public event"}
                </GText>
              </>
            )}
          </View>

          {/* Description */}
          <View style={[styles.section, { marginBottom: 24 * vScale }]}>
            <DisplayDescription
              description={displayDescription}
              editable={editable}
              onPress={() => setModalSection("description")}
              vScale={vScale}
            />
          </View>

          {/* Flyer link */}
          {editable && (
            <View style={[styles.section, { marginBottom: 24 * vScale }]}>
              <Pressable
                disabled={uploading}
                onPress={() => fileInputRef.current?.click()}
              >
                <GText style={styles.flyerLinkText}>
                  {uploading
                    ? uploadStatus === "compressing"
                      ? "compressing..."
                      : `uploading ${uploadProgress}%`
                    : event.flyer_url
                      ? "+ edit background image"
                      : "+ add background image"}
                </GText>
              </Pressable>
              {Platform.OS === "web" && (
                <input
                  accept={ACCEPT_STRING}
                  onChange={handleFileSelect}
                  ref={fileInputRef}
                  style={styles.hidden}
                  type="file"
                />
              )}
            </View>
          )}
          {isAdmin && <GText style={styles.statusText}>{event.status}</GText>}

          {displayError ? (
            <GText style={styles.error}>{displayError}</GText>
          ) : null}
        </DarkBlurCard>
      </ScrollView>

      {/* Bottom button */}
      <BottomEventButton
        disabled={saving || getBottomLabel === "processing..."}
        label={getBottomLabel}
        onPress={handleBottomOnPress}
        saving={saving && mode !== "view"}
        vScale={vScale}
      />

      {mode === "view" && paymentUI}

      {/* Guest list modal */}
      {mode === "view" && (
        <GuestListModal
          eventId={event.id}
          guests={guests}
          isAdmin={isAdmin}
          onClose={() => setGuestListVisible(false)}
          visible={guestListVisible}
        />
      )}

      {/* RSVP modal (no-ticket events) */}
      {mode === "view" && (
        <RsvpModal
          onClose={() => setRsvpVisible(false)}
          onSelect={handleRsvp}
          visible={rsvpVisible}
        />
      )}

      {/* Sign in modal */}
      <SignInModal
        onClose={() => setSignInVisible(false)}
        onSignedIn={() => setSignInVisible(false)}
        visible={signInVisible}
      />

      {/* Cancel event confirmation */}
      <AppModal
        onClose={() => setShowCancelConfirm(false)}
        visible={showCancelConfirm}
      >
        <GText style={styles.cancelConfirmText}>
          are you sure you want to cancel the event?
        </GText>
        <Pressable
          onPress={() => {
            eventEdit?.patchEvent({ status: "cancelled" });
            setShowCancelConfirm(false);
          }}
          style={styles.cancelConfirmButton}
        >
          <GText style={styles.cancelConfirmButtonText}>confirm</GText>
        </Pressable>
      </AppModal>

      {/* Section edit modals (edit/create only) */}
      {editable && eventEdit && (
        <SectionEditModal
          event={event}
          hasExistingTicket={hasTicket}
          onClose={() => setModalSection(null)}
          onEventChange={(patch) => eventEdit.patchEvent(patch)}
          onRemoveTicket={
            onRemoveTicket
              ? () => {
                  onRemoveTicket();
                  setModalSection(null);
                }
              : undefined
          }
          onTicketChange={(patch) => eventEdit.patchTicketKind(patch)}
          section={modalSection}
        />
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  actionText: {
    color: colors.white,
    fontSize: 14,
    fontWeight: "600",
    marginLeft: 8,
    textAlign: "right",
  },
  bottomButton: {
    alignItems: "center",
    alignSelf: "center",
    backgroundColor: colors.white,
    borderRadius: 28,
    marginBottom: 32,
    maxWidth: 600,
    paddingHorizontal: 32,
    paddingVertical: 16,
    width: "100%",
  },
  bottomContainer: {
    bottom: 0,
    left: 0,
    paddingHorizontal: 24,
    paddingTop: 12,
    position: "absolute",
    right: 0,
  },
  bottomSpacer: {
    height: 80,
  },
  bottomText: {
    color: colors.black,
    fontSize: 18,
    fontWeight: "600",
  },
  cancelConfirmButton: {
    alignItems: "center" as const,
    backgroundColor: colors.red,
    borderRadius: 12,
    marginTop: 16,
    paddingVertical: 12,
  },
  cancelConfirmButtonText: {
    color: colors.white,
    fontSize: 16,
    fontWeight: "600" as const,
  },
  cancelConfirmText: {
    color: colors.white,
    fontSize: 18,
    textAlign: "center" as const,
  },
  cancelText: {
    color: colors.white,
    fontSize: 16,
    fontWeight: "600",
    width: 60,
  },
  container: {
    backgroundColor: colors.transparent,
    flex: 1,
    paddingHorizontal: HORIZONTAL_MARGIN,
  },
  content: {
    alignItems: "center",
    alignSelf: "center",
    width: "100%",
  },
  detailPrimary: {
    color: colors.white,
    fontSize: 18,
    fontWeight: "600",
    textAlign: "left",
  },
  detailSecondary: {
    color: colors.white,
    fontSize: 14,
    opacity: 0.8,
    textAlign: "left",
  },
  disabledOpacity: {
    opacity: 0.5,
  },
  editButton: {
    borderColor: colors.white,
    borderRadius: 16,
    borderWidth: 1,
    paddingHorizontal: 14,
    paddingVertical: 6,
  },
  editText: {
    color: colors.white,
    fontSize: 14,
    fontWeight: "600",
  },
  error: {
    color: colors.red,
    fontSize: 14,
    marginTop: 8,
  },
  fixedHeader: {
    alignItems: "center",
    alignSelf: "center",
    flexDirection: "row",
    height: HEADER_HEIGHT,
    left: 0,
    marginHorizontal: HORIZONTAL_MARGIN,
    position: "absolute",
    right: 0,
    top: 0,
    zIndex: 10,
  },
  flexFill: {
    flex: 1,
  },
  flyerLinkText: {
    color: colors.white,
    fontSize: 15,
    lineHeight: 22,
    marginBottom: 8,
    textAlign: "left",
  },
  headerContainer: {
    left: 10,
    position: "absolute",
    right: 10,
    top: 0,
  },
  headerInner: {
    alignItems: "center",
    alignSelf: "center",
    flexDirection: "row",
    height: HEADER_HEIGHT,
    justifyContent: "space-between",
    maxWidth: 600,
    width: "100%",
    zIndex: 10,
  },
  headerRow: {
    alignItems: "center",
    flexDirection: "row",
    justifyContent: "center",
    marginBottom: 8,
  },
  headerSpacer: {
    width: 50,
  },
  hidden: {
    display: "none",
  },
  hostedBy: {
    color: colors.light,
    fontSize: 14,
    marginBottom: 12,
    textAlign: "left",
    width: "100%",
  },
  iconColumn: {
    alignItems: "center",
    paddingRight: 10,
    width: 32,
  },
  infoGrid: {
    marginBottom: 16,
    width: "100%",
  },
  infoRow: {
    alignItems: "center",
    flexDirection: "row",
    marginBottom: 12,
  },
  rowHighlight: {
    backgroundColor: colors.cardBg,
    borderRadius: 12,
  },
  rsvpNames: {
    color: colors.white,
    fontSize: 15,
    fontWeight: "600",
  },
  rsvpSection: {
    marginBottom: 16,
    width: "100%",
  },
  scroll: {
    flex: 1,
  },
  section: {
    marginBottom: 24,
    width: "100%",
  },
  statusLabel: {
    color: colors.light,
    fontSize: 14,
    fontWeight: "500",
    marginRight: 20,
  },
  statusPill: {
    borderColor: colors.divider,
    borderRadius: 20,
    borderWidth: 1.5,
    paddingHorizontal: 16,
    paddingVertical: 8,
  },
  statusPillActive: {
    backgroundColor: colors.white,
    borderColor: colors.white,
  },
  statusPillCancelled: {
    backgroundColor: colors.red,
    borderColor: colors.red,
  },
  statusPillDisabled: {
    opacity: 0.3,
  },
  statusPillRow: {
    flexDirection: "row",
    gap: 10,
  },
  statusPillText: {
    color: colors.white,
    fontSize: 14,
    fontWeight: "500",
  },
  statusPillTextActive: {
    color: colors.black,
  },
  statusPillTextCancelled: {
    color: colors.white,
  },
  statusRow: {
    alignItems: "center",
    flexDirection: "row",
    justifyContent: "space-between",
    marginBottom: 16,
    width: "100%",
  },
  statusText: {
    color: colors.light,
    fontSize: 14,
    textAlign: "center",
  },
  textColumn: {
    flex: 1,
  },
  title: {
    color: colors.light,
    flex: 1,
    fontWeight: "700",
    textAlign: "left",
  },
});
